import { useState } from 'react';
import type { AlertaCustoFicha, EtapaProcessoResponse, MaterialFichaResponse, NoCascataResponse } from '@triade/contracts';
import { useCascata } from '@/api/fichas';
import { ErroFormacao } from '@/components/formacao/avisos-formacao';
import { ApiError } from '@/lib/api';
import { rotuloUnidade } from '@/lib/i18n-pt';
import { formatarCusto, formatarQuantidade } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Etiqueta } from './grade-comum';
import { mesCurto, ROTULO_ERRO_CUSTO, ROTULO_TIPO_COMPONENTE } from './tipos';

const ORIGEM: Record<MaterialFichaResponse['origemCusto'], string> = { AQUISICAO: 'Aquisição', SEMIACABADO: 'Semiacabado', COPRODUTO: 'Coproduto', INFORMADO: 'Informado' };

/** Caminho que a API devolve no erro de ciclo/profundidade (`details.caminho`), ou vazio. */
function caminhoDoErro(erro: ApiError): string[] {
  const d = erro.body?.details as { caminho?: unknown } | undefined;
  return Array.isArray(d?.caminho) ? d.caminho.map(String) : [];
}

/** Erros de cascata com o CAMINHO (A -> B -> A): o que a API mandou, mais a explicacao da tela. */
function ErroCascata({ erro, onTentar }: { erro: unknown; onTentar: () => void }) {
  if (erro instanceof ApiError && erro.status === 403) {
    return (
      <section role="alert" data-testid="cascata-sem-acesso" className="rounded-xl border border-border bg-card px-4 py-3.5 text-small text-ink-secondary">
        <div className="font-semibold text-ink">Sem acesso à ficha em cascata</div>
        <div className="mt-1">Esta visão mostra a estrutura de semiacabados e exige permissão de consulta de fichas técnicas (ficha.ver).</div>
      </section>
    );
  }
  if (erro instanceof ApiError && (erro.code === 'CICLO_NA_FICHA' || erro.code === 'PROFUNDIDADE_FICHA_EXCEDIDA')) {
    const caminho = caminhoDoErro(erro);
    return (
      <section role="alert" data-testid={`cascata-${erro.code === 'CICLO_NA_FICHA' ? 'ciclo' : 'profundidade'}`} className="rounded-xl border border-destructive/30 bg-danger-bg px-4 py-3.5 text-small text-danger-ink">
        <div className="font-semibold">{erro.code === 'CICLO_NA_FICHA' ? 'Ciclo na cascata de semiacabados' : 'Cascata de semiacabados profunda demais'}</div>
        <div className="mt-1">{ROTULO_ERRO_CUSTO[erro.code] ?? erro.message}</div>
        {caminho.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="cascata-caminho">
            <span className="text-ink-secondary">Caminho:</span>
            {caminho.map((c, i) => (
              <span key={`${c}-${i}`} className="flex items-center gap-1.5">
                <Etiqueta>{c}</Etiqueta>
                {i < caminho.length - 1 && <span aria-hidden="true" className="text-ink-muted">→</span>}
              </span>
            ))}
          </div>
        )}
        <div className="mt-2 text-ink-secondary">Corrija o componente que fecha o ciclo na ficha (Ficha técnica, aba Ficha) e tente de novo.</div>
        <div className="tabular mt-1 text-caption text-ink-muted">código {erro.code}</div>
      </section>
    );
  }
  return <ErroFormacao titulo="Não foi possível carregar a cascata" erro={erro} onTentar={onTentar} />;
}

/**
 * Tela "Ficha em cascata" (GET /fichas-tecnicas/:id/cascata): arvore por nivel, expansivel. Cada nivel tem o processo
 * (etapas), os materiais, o custo do nivel e o total acumulado. Valores so onde a API manda (PCP: estrutura sem R$).
 */
export function CascataFicha({ fichaId }: { fichaId: string }) {
  const [dataTexto, setDataTexto] = useState('');
  const cascata = useCascata(fichaId, dataTexto || undefined);

  return (
    <section aria-label="Ficha em cascata" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3 rounded-2xl border border-border bg-card px-5 py-4">
        <div>
          <h2 className="text-title font-semibold">Ficha em cascata</h2>
          <div className="text-small text-ink-muted">
            Cada nível é uma ficha (o produto e, abaixo, os semiacabados). Quantidade por unidade do pai; o total acumulado soma os filhos.
          </div>
        </div>
        <label className="flex flex-col gap-1 text-caption font-medium text-ink-muted">
          Data do CPM (vazio = hoje)
          <input
            type="date"
            aria-label="Data do CPM"
            value={dataTexto}
            onChange={(e) => setDataTexto(e.target.value)}
            className="h-9 rounded-[10px] border border-input bg-card px-3 text-body font-normal text-foreground outline-none focus-visible:border-primary focus-visible:shadow-focus"
          />
        </label>
      </div>

      {cascata.isPending && (
        <p role="status" className="rounded-2xl border border-dashed border-border bg-card px-5 py-10 text-center text-small text-ink-muted">
          Carregando cascata…
        </p>
      )}
      {cascata.isError && <ErroCascata erro={cascata.error} onTentar={() => void cascata.refetch()} />}

      {cascata.data && (
        <>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-small text-ink-secondary" data-testid="cascata-resumo">
            <span>
              Níveis: <span className="tabular font-medium text-ink">{cascata.data.profundidade}</span>
            </span>
            <span>
              Data: <span className="tabular font-medium text-ink">{cascata.data.data}</span>
            </span>
          </div>
          {cascata.data.estadoCusto === 'SEM_CPM' && (
            <section role="status" data-testid="cascata-sem-cpm" className="rounded-xl border border-warning-dot/40 bg-warning-bg px-4 py-3 text-small text-warning-ink">
              <div className="font-semibold">Sem CPM para calcular a cascata</div>
              <div className="mt-1">Há centros sem custo por minuto até a data. A estrutura aparece sem valores; o custo volta quando o CPM for cadastrado.</div>
              <AlertasCusto alertas={cascata.data.alertasCusto} />
            </section>
          )}
          {cascata.data.estadoCusto !== 'SEM_CPM' && <AlertasCusto alertas={cascata.data.alertasCusto} />}
          <ul className="flex flex-col gap-3" aria-label="Árvore da cascata">
            <No no={cascata.data.raiz} />
          </ul>
        </>
      )}
    </section>
  );
}

function AlertasCusto({ alertas }: { alertas: AlertaCustoFicha[] }) {
  const cpm = alertas.filter((a) => a.code === 'CPM_ANTIGO');
  if (cpm.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {cpm.map((a, i) => (
        <span key={`${a.centroCodigo ?? ''}-${i}`} className="rounded-full bg-card px-2.5 py-0.5 text-caption font-medium">
          CPM antigo ({mesCurto(a.competencia)}) · {a.centroCodigo ?? 'centro'}
        </span>
      ))}
    </div>
  );
}

/** Um no da arvore: cabecalho expansivel (nivel, item, versao, quantidade, custo do nivel, total acumulado) e os filhos. */
function No({ no }: { no: NoCascataResponse }) {
  const [aberto, setAberto] = useState(no.nivel <= 2);
  const temCusto = no.totalAcumulado !== undefined;
  const caminho = no.caminho.join(' › ');
  const corpoId = `no-${no.fichaId}`;
  return (
    <li data-testid="no-cascata" data-nivel={no.nivel} className="list-none">
      <div className={cn('rounded-2xl border border-border bg-card', no.nivel > 1 && 'ml-6')}>
        <button
          type="button"
          aria-expanded={aberto}
          aria-controls={corpoId}
          onClick={() => setAberto((v) => !v)}
          className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 px-5 py-3 text-left"
        >
          <span aria-hidden="true" className="text-ink-muted">{aberto ? '▾' : '▸'}</span>
          <span className="text-caption font-medium text-ink-muted">Nível {no.nivel}</span>
          <Etiqueta>{no.itemCodigo}</Etiqueta>
          <span className="font-medium">{no.itemDescricao}</span>
          <span className="tabular text-caption text-ink-secondary">v{no.versao}</span>
          {no.quantidade !== null && <span className="tabular text-caption text-ink-secondary">× {formatarQuantidade(no.quantidade)}</span>}
          {no.nivel > 1 && <span className="text-caption text-ink-muted" title="Caminho desde a ficha pedida">{caminho}</span>}
          <span className="ml-auto flex flex-wrap items-center gap-x-5 gap-y-1 text-small">
            {no.custoNivel !== undefined && (
              <span>
                <span className="text-ink-muted">Custo do nível </span>
                <span className="tabular" data-testid="no-custo-nivel">{formatarCusto(no.custoNivel)}</span>
              </span>
            )}
            {temCusto && (
              <span>
                <span className="text-ink-muted">Total acumulado </span>
                <span className="tabular font-semibold" data-testid="no-total-acumulado">{formatarCusto(no.totalAcumulado!)}</span>
              </span>
            )}
          </span>
        </button>

        {aberto && (
          <div id={corpoId} className="flex flex-col gap-4 border-t border-border px-5 py-4">
            <Processo etapas={no.processo.etapas} nivel={no.nivel} />
            <Materiais itens={no.materiais.itens} total={no.materiais.total} nivel={no.nivel} />
            {no.filhos.length > 0 && (
              <ul className="flex flex-col gap-3" aria-label={`Semiacabados do nível ${no.nivel}`}>
                {no.filhos.map((f) => (
                  <No key={`${f.fichaId}-${f.caminho.join('/')}`} no={f} />
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

/** Etapas do nivel. Sem `cpm`/`custo` na resposta (PCP) as colunas de R$ nao existem. */
function Processo({ etapas, nivel }: { etapas: EtapaProcessoResponse[]; nivel: number }) {
  if (etapas.length === 0) return null;
  const comValores = etapas.some((e) => e.custo !== undefined);
  return (
    <div>
      <div className="mb-1 text-caption font-medium uppercase tracking-wide text-ink-muted">Processo</div>
      <table className="w-full text-small" aria-label={`Etapas do nível ${nivel}`}>
        <thead>
          <tr className="text-left text-caption text-ink-muted">
            <th className="pb-1 font-medium">Centro</th>
            <th className="pb-1 text-right font-medium">Minutos</th>
            {comValores && <th className="pb-1 text-right font-medium">CPM (mês)</th>}
            {comValores && <th className="pb-1 text-right font-medium">Custo</th>}
          </tr>
        </thead>
        <tbody>
          {etapas.map((e, i) => (
            <tr key={e.componenteId} className="border-t border-border" data-testid="cascata-etapa">
              <td className="py-1.5">
                <span className="flex items-center gap-2">
                  <Etiqueta>{e.centroCaminho}</Etiqueta>
                  <span>{`${i + 1}. ${e.centroNome}`}</span>
                  {e.descricao && <span className="text-caption text-ink-muted" data-testid="cascata-etapa-observacao">{e.descricao}</span>}
                  {e.cpmAntigo && <span className="rounded-full bg-warning-bg px-2 text-caption font-medium text-warning-ink">CPM antigo ({mesCurto(e.competencia)})</span>}
                </span>
              </td>
              <td className="tabular py-1.5 text-right">{formatarQuantidade(e.minutos)}</td>
              {comValores && <td className="tabular py-1.5 text-right">{e.cpm !== undefined ? formatarCusto(e.cpm) : '—'}</td>}
              {comValores && <td className="tabular py-1.5 text-right">{e.custo !== undefined ? formatarCusto(e.custo) : '—'}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Materiais do nivel (inclui os semiacabados). Sem `custoTotal` (PCP) as colunas de R$ nao existem. */
function Materiais({ itens, total, nivel }: { itens: MaterialFichaResponse[]; total?: string; nivel: number }) {
  if (itens.length === 0) return null;
  const comValores = itens.some((m) => m.custoTotal !== undefined);
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-3 text-caption font-medium uppercase tracking-wide text-ink-muted">
        <span>Materiais</span>
        {comValores && total !== undefined && (
          <span className="normal-case tracking-normal">
            Total dos materiais <span className="tabular font-semibold text-ink">{formatarCusto(total)}</span>
          </span>
        )}
      </div>
      <table className="w-full text-small" aria-label={`Materiais do nível ${nivel}`}>
        <thead>
          <tr className="text-left text-caption text-ink-muted">
            <th className="pb-1 font-medium">Componente</th>
            <th className="pb-1 font-medium">Tipo</th>
            <th className="pb-1 text-right font-medium">Qtd.</th>
            <th className="pb-1 font-medium">Origem</th>
            {comValores && <th className="pb-1 text-right font-medium">Custo unit.</th>}
            {comValores && <th className="pb-1 text-right font-medium">Total</th>}
          </tr>
        </thead>
        <tbody>
          {itens.map((m) => (
            <tr key={m.componenteId} className="border-t border-border" data-testid="cascata-material">
              <td className="py-1.5">
                <span className="flex flex-wrap items-center gap-2">
                  {m.itemCodigo && <Etiqueta>{m.itemCodigo}</Etiqueta>}
                  <span>{m.descricao}</span>
                  {m.alertas?.includes('MAO_DE_OBRA_FORA_DO_PROCESSO') && (
                    <span className="rounded-full bg-destructive/10 px-2 text-caption font-medium text-danger-ink">Mão de obra fora do processo</span>
                  )}
                </span>
              </td>
              <td className="py-1.5 text-ink-secondary">{ROTULO_TIPO_COMPONENTE[m.tipo]}</td>
              <td className="tabular py-1.5 text-right">
                {formatarQuantidade(m.quantidade)}
                {m.unidade ? ` ${rotuloUnidade(m.unidade)}` : ''}
              </td>
              <td className="py-1.5 text-ink-secondary">{ORIGEM[m.origemCusto]}</td>
              {comValores && <td className="tabular py-1.5 text-right">{m.custoUnitario !== undefined ? formatarCusto(m.custoUnitario) : '—'}</td>}
              {comValores && <td className="tabular py-1.5 text-right">{m.custoTotal !== undefined ? formatarCusto(m.custoTotal) : '—'}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
